import {
  APPLE_CHIPS,
  GPUS,
  MACHINE_PRESETS,
  MODELS,
  RUNTIMES,
  findAppleChip,
  findGpu,
  findMachine,
  findModel,
  findRuntime,
} from './catalog.js';
import { estimateFit, recommendQuantForMachine, recommendRuntimeForMachine } from './fit.js';
import { recommendForMachine } from './recommend.js';
import type { DirectoryListing, Quantization } from './types.js';

export function slugFor(pageType: DirectoryListing['pageType'], id: string, secondary?: string): string {
  if (pageType === 'can-i-run' && secondary) {
    return `can-i-run/${id}-on-${secondary}`;
  }
  if (pageType === 'compare' && secondary) {
    return `compare/${id}-vs-${secondary}`;
  }
  if (pageType === 'leaderboard' && secondary) {
    return `leaderboard/${id}-${secondary}`;
  }
  if (pageType === 'machine' || pageType === 'gpu' || pageType === 'apple-chip') {
    return `local-models/${id}`;
  }
  if (pageType === 'model') {
    return `models/${id}`;
  }
  if (pageType === 'runtime') {
    return `runtimes/${id}`;
  }
  return `${pageType}/${id}`;
}

function machineListing(machineId: string): DirectoryListing {
  const machine = findMachine(machineId);
  if (!machine) throw new Error(`Unknown machine: ${machineId}`);
  const top = recommendForMachine(machine, 'best-on-machine', 5);
  const avgTps = top.reduce((s, r) => s + r.fit.expectedTps, 0) / Math.max(1, top.length);
  const avgQuality = top.reduce((s, r) => s + r.model.baseQualityScore, 0) / Math.max(1, top.length);
  return {
    slug: slugFor('machine', machine.id),
    pageType: 'machine',
    title: `Best local coding models for ${machine.nickname}`,
    subtitle: `${machine.accel} · ${machine.ramGb}GB RAM · ${machine.vramGb}GB usable`,
    primary: { kind: 'machine', id: machine.id },
    topModels: top.map((r) => ({
      modelId: r.model.id,
      quantization: r.fit.quantization,
      verdict: r.fit.verdict,
      score: Math.round(r.score),
    })),
    aggregate: {
      benchRuns: top.length,
      avgTps: Math.round(avgTps * 10) / 10,
      avgQuality: Math.round(avgQuality),
    },
    updatedAt: Date.now(),
  };
}

function gpuListing(gpuId: string): DirectoryListing {
  const gpu = findGpu(gpuId);
  if (!gpu) throw new Error(`Unknown GPU: ${gpuId}`);
  const representativeMachine = MACHINE_PRESETS.find((m) => m.gpuId === gpuId) ?? {
    id: `synthetic-${gpu.id}`,
    nickname: `${gpu.label} desktop`,
    os: 'linux' as const,
    cpuLabel: 'Generic CPU',
    accel: 'cuda' as const,
    gpuId: gpu.id,
    ramGb: 64,
    vramGb: gpu.vramGb,
    storageFreeGb: 1000,
    installedRuntimes: ['ollama', 'llama-cpp'] as const,
    preferredRuntime: 'llama-cpp' as const,
  };
  const top = recommendForMachine(representativeMachine, 'best-on-machine', 5);
  return {
    slug: slugFor('gpu', gpu.id),
    pageType: 'gpu',
    title: `Best local coding models for ${gpu.label}`,
    subtitle: `${gpu.vramGb}GB VRAM · ${gpu.memoryBandwidthGbps} GB/s bandwidth`,
    primary: { kind: 'gpu', id: gpu.id },
    topModels: top.map((r) => ({
      modelId: r.model.id,
      quantization: r.fit.quantization,
      verdict: r.fit.verdict,
      score: Math.round(r.score),
    })),
    aggregate: {
      benchRuns: top.length,
      avgTps: Math.round(top.reduce((s, r) => s + r.fit.expectedTps, 0) / Math.max(1, top.length)),
      avgQuality: Math.round(top.reduce((s, r) => s + r.model.baseQualityScore, 0) / Math.max(1, top.length)),
    },
    updatedAt: Date.now(),
  };
}

function appleChipListing(chipId: string): DirectoryListing {
  const chip = findAppleChip(chipId);
  if (!chip) throw new Error(`Unknown Apple chip: ${chipId}`);
  const representativeMachine = MACHINE_PRESETS.find((m) => m.appleChipId === chipId) ?? {
    id: `synthetic-${chip.id}`,
    nickname: `${chip.label} Mac`,
    os: 'macos' as const,
    cpuLabel: chip.label,
    accel: 'apple-silicon' as const,
    appleChipId: chip.id,
    ramGb: chip.tier === 'base' ? 16 : chip.tier === 'pro' ? 32 : chip.tier === 'max' ? 48 : 128,
    vramGb: chip.tier === 'base' ? 14 : chip.tier === 'pro' ? 28 : chip.tier === 'max' ? 42 : 110,
    storageFreeGb: 500,
    installedRuntimes: ['ollama', 'mlx'] as const,
    preferredRuntime: 'mlx' as const,
  };
  const top = recommendForMachine(representativeMachine, 'best-on-machine', 5);
  return {
    slug: slugFor('apple-chip', chip.id),
    pageType: 'apple-chip',
    title: `Best local coding models for ${chip.label}`,
    subtitle: `${chip.gpuCores} GPU cores · ${chip.memoryBandwidthGbps} GB/s unified memory`,
    primary: { kind: 'apple-chip', id: chip.id },
    topModels: top.map((r) => ({
      modelId: r.model.id,
      quantization: r.fit.quantization,
      verdict: r.fit.verdict,
      score: Math.round(r.score),
    })),
    aggregate: {
      benchRuns: top.length,
      avgTps: Math.round(top.reduce((s, r) => s + r.fit.expectedTps, 0) / Math.max(1, top.length)),
      avgQuality: Math.round(top.reduce((s, r) => s + r.model.baseQualityScore, 0) / Math.max(1, top.length)),
    },
    updatedAt: Date.now(),
  };
}

function modelListing(modelId: string): DirectoryListing {
  const model = findModel(modelId);
  if (!model) throw new Error(`Unknown model: ${modelId}`);
  const tops = MACHINE_PRESETS.map((machine) => {
    const quant = recommendQuantForMachine(machine, model);
    const runtime = recommendRuntimeForMachine(machine);
    const fit = estimateFit(machine, model, quant, runtime);
    return { machine, fit };
  }).sort((a, b) => b.fit.expectedTps - a.fit.expectedTps);

  return {
    slug: slugFor('model', model.id),
    pageType: 'model',
    title: `${model.displayName} — local hardware fit`,
    subtitle: `${model.paramsB}B params · ${model.useCase} · ${model.license}`,
    primary: { kind: 'model', id: model.id },
    topModels: tops.slice(0, 5).map((t) => ({
      modelId: t.machine.id,
      quantization: t.fit.quantization as Quantization,
      verdict: t.fit.verdict,
      score: Math.round(t.fit.expectedTps * 10),
    })),
    aggregate: {
      benchRuns: tops.length,
      avgTps: Math.round(tops.reduce((s, r) => s + r.fit.expectedTps, 0) / Math.max(1, tops.length)),
      avgQuality: model.baseQualityScore,
    },
    updatedAt: Date.now(),
  };
}

function runtimeListing(runtimeId: string): DirectoryListing {
  const runtime = findRuntime(runtimeId);
  if (!runtime) throw new Error(`Unknown runtime: ${runtimeId}`);
  const compatibleMachines = MACHINE_PRESETS.filter((m) => runtime.accelerators.includes(m.accel));
  const sampleFits = compatibleMachines.slice(0, 5).flatMap((machine) =>
    MODELS.slice(0, 3).map((model) => {
      const quant = recommendQuantForMachine(machine, model);
      return estimateFit(machine, model, quant, runtime.id);
    }),
  );
  const avgTps =
    sampleFits.length === 0 ? 0 : sampleFits.reduce((s, f) => s + f.expectedTps, 0) / sampleFits.length;
  return {
    slug: slugFor('runtime', runtime.id),
    pageType: 'runtime',
    title: `${runtime.label} — what to run where`,
    subtitle: runtime.notes,
    primary: { kind: 'runtime', id: runtime.id },
    topModels: compatibleMachines.slice(0, 5).map((machine) => {
      const best = MODELS[0];
      const quant = recommendQuantForMachine(machine, best);
      const fit = estimateFit(machine, best, quant, runtime.id);
      return {
        modelId: machine.id,
        quantization: quant,
        verdict: fit.verdict,
        score: Math.round(fit.expectedTps * 10),
      };
    }),
    aggregate: {
      benchRuns: sampleFits.length,
      avgTps: Math.round(avgTps * 10) / 10,
      avgQuality: 0,
    },
    updatedAt: Date.now(),
  };
}

export function compareListing(modelAId: string, modelBId: string): DirectoryListing {
  const a = findModel(modelAId);
  const b = findModel(modelBId);
  if (!a || !b) throw new Error(`Unknown pair: ${modelAId} vs ${modelBId}`);
  const machineResults = MACHINE_PRESETS.slice(0, 5).map((machine) => {
    const qa = recommendQuantForMachine(machine, a);
    const qb = recommendQuantForMachine(machine, b);
    const runtime = recommendRuntimeForMachine(machine);
    const fa = estimateFit(machine, a, qa, runtime);
    const fb = estimateFit(machine, b, qb, runtime);
    return { machine, fa, fb };
  });
  const aWins = machineResults.filter((r) => r.fa.expectedTps >= r.fb.expectedTps).length;
  const bWins = machineResults.length - aWins;
  return {
    slug: slugFor('compare', a.id, b.id),
    pageType: 'compare',
    title: `${a.displayName} vs ${b.displayName}`,
    subtitle: `${aWins} machines favor ${a.displayName} · ${bWins} favor ${b.displayName}`,
    primary: { kind: 'pair', id: a.id, secondaryId: b.id },
    topModels: machineResults.map((r) => ({
      modelId: r.machine.id,
      quantization: r.fa.expectedTps >= r.fb.expectedTps ? r.fa.quantization : r.fb.quantization,
      verdict: r.fa.expectedTps >= r.fb.expectedTps ? r.fa.verdict : r.fb.verdict,
      score: Math.round(Math.max(r.fa.expectedTps, r.fb.expectedTps) * 10),
    })),
    aggregate: {
      benchRuns: machineResults.length,
      avgTps:
        Math.round(
          (machineResults.reduce((s, r) => s + Math.max(r.fa.expectedTps, r.fb.expectedTps), 0) /
            Math.max(1, machineResults.length)) *
            10,
        ) / 10,
      avgQuality: Math.round((a.baseQualityScore + b.baseQualityScore) / 2),
    },
    updatedAt: Date.now(),
  };
}

export function canIRunListing(modelId: string, machineId: string): DirectoryListing {
  const model = findModel(modelId);
  const machine = findMachine(machineId);
  if (!model || !machine) throw new Error(`Unknown pair: ${modelId} on ${machineId}`);
  const quant = recommendQuantForMachine(machine, model);
  const runtime = recommendRuntimeForMachine(machine);
  const fit = estimateFit(machine, model, quant, runtime);
  return {
    slug: slugFor('can-i-run', model.id, machine.id),
    pageType: 'can-i-run',
    title: `Can I run ${model.displayName} on ${machine.nickname}?`,
    subtitle: fit.verdictHeadline,
    primary: { kind: 'pair', id: model.id, secondaryId: machine.id },
    topModels: [
      {
        modelId: model.id,
        quantization: quant,
        verdict: fit.verdict,
        score: Math.round(fit.expectedTps * 10),
      },
    ],
    aggregate: { benchRuns: 1, avgTps: fit.expectedTps, avgQuality: model.baseQualityScore },
    updatedAt: Date.now(),
  };
}

export function leaderboardListing(
  machineId: string,
  task: 'coding' | 'review' | 'debugging' | 'reasoning' | 'docs' | 'tool-use',
): DirectoryListing {
  const machine = findMachine(machineId);
  if (!machine) throw new Error(`Unknown machine: ${machineId}`);
  const picks = recommendForMachine(machine, 'best-coding', 5);
  return {
    slug: slugFor('leaderboard', machine.id, task),
    pageType: 'leaderboard',
    title: `Top ${task} models on ${machine.nickname}`,
    subtitle: 'Ranked by projected quality × tok/s × headroom.',
    primary: { kind: 'machine', id: machine.id, secondaryId: task },
    topModels: picks.map((p) => ({
      modelId: p.model.id,
      quantization: p.fit.quantization,
      verdict: p.fit.verdict,
      score: Math.round(p.score),
    })),
    aggregate: {
      benchRuns: picks.length,
      avgTps:
        Math.round((picks.reduce((s, p) => s + p.fit.expectedTps, 0) / Math.max(1, picks.length)) * 10) / 10,
      avgQuality: Math.round(
        picks.reduce((s, p) => s + p.model.baseQualityScore, 0) / Math.max(1, picks.length),
      ),
    },
    updatedAt: Date.now(),
  };
}

const FEATURED_COMPARES: Array<[string, string]> = [
  ['qwen-2.5-coder-14b', 'deepseek-coder-v2-16b'],
  ['qwen-2.5-coder-32b', 'codestral-22b'],
  ['qwen-2.5-coder-7b', 'qwen-2.5-coder-3b'],
  ['llama-3.3-70b', 'qwen-2.5-coder-32b'],
  ['phi-4-14b', 'qwen-2.5-coder-14b'],
  ['code-llama-34b', 'codestral-22b'],
];

const FEATURED_RUNTIME_COMPARES: Array<[string, string]> = [
  ['ollama', 'lm-studio'],
  ['ollama', 'llama-cpp'],
  ['mlx', 'ollama'],
  ['llama-cpp', 'vllm'],
];

const FEATURED_CAN_I_RUN: Array<[string, string]> = [
  ['qwen-2.5-coder-14b', 'macbook-air-m2-16'],
  ['qwen-2.5-coder-32b', 'macbook-pro-m3-max'],
  ['qwen-2.5-coder-32b', 'macbook-pro-m4-max-64'],
  ['qwen-2.5-coder-32b', 'rtx-4090-desktop-64'],
  ['llama-3.3-70b', 'macbook-pro-m4-max-64'],
  ['llama-3.3-70b', 'rtx-4090-desktop-64'],
  ['deepseek-coder-v2-16b', 'mac-mini-m4-24'],
  ['codestral-22b', 'rtx-3060-12-pc'],
  ['phi-4-14b', 'macbook-air-m2-16'],
  ['qwen-2.5-coder-7b', 'macbook-air-m2-16'],
];

export function runtimeCompareListing(runtimeA: string, runtimeB: string): DirectoryListing {
  const a = findRuntime(runtimeA);
  const b = findRuntime(runtimeB);
  if (!a || !b) throw new Error(`Unknown runtime pair: ${runtimeA} vs ${runtimeB}`);
  const overlap = a.accelerators.filter((acc) => b.accelerators.includes(acc));
  const aBeginner = a.beginnerFriendly;
  const bBeginner = b.beginnerFriendly;
  return {
    slug: slugFor('runtime', `${a.id}-vs-${b.id}`),
    pageType: 'runtime',
    title: `${a.label} vs ${b.label}`,
    subtitle: `${overlap.length > 0 ? `Both run on ${overlap.join(', ')}. ` : ''}${aBeginner && !bBeginner ? `${a.label} is beginner-friendlier.` : !aBeginner && bBeginner ? `${b.label} is beginner-friendlier.` : 'Both are power-user grade.'}`,
    primary: { kind: 'runtime', id: a.id, secondaryId: b.id },
    topModels: [],
    aggregate: { benchRuns: 0, avgTps: 0, avgQuality: 0 },
    updatedAt: Date.now(),
  };
}

export function allListings(): DirectoryListing[] {
  const listings: DirectoryListing[] = [];
  for (const m of MACHINE_PRESETS) listings.push(machineListing(m.id));
  for (const g of GPUS) listings.push(gpuListing(g.id));
  for (const c of APPLE_CHIPS) listings.push(appleChipListing(c.id));
  for (const model of MODELS) listings.push(modelListing(model.id));
  for (const r of RUNTIMES) listings.push(runtimeListing(r.id));
  for (const [a, b] of FEATURED_COMPARES) listings.push(compareListing(a, b));
  for (const [a, b] of FEATURED_RUNTIME_COMPARES) listings.push(runtimeCompareListing(a, b));
  for (const [model, machine] of FEATURED_CAN_I_RUN) listings.push(canIRunListing(model, machine));
  for (const u of useCaseLandingPages()) listings.push(u);
  const LEADERBOARD_TASKS = ['coding', 'tool-use'] as const;
  for (const m of MACHINE_PRESETS) {
    for (const t of LEADERBOARD_TASKS) listings.push(leaderboardListing(m.id, t));
  }
  return listings;
}

function useCaseLandingPages(): DirectoryListing[] {
  const out: DirectoryListing[] = [];
  out.push({
    slug: 'local-models/best-for-coding',
    pageType: 'machine',
    title: 'Best local coding models right now',
    subtitle: 'Top picks across every machine preset.',
    primary: { kind: 'machine', id: 'best-for-coding' },
    topModels: MACHINE_PRESETS.slice(0, 5).map((m) => {
      const top = recommendForMachine(m, 'best-coding', 1)[0];
      return {
        modelId: top?.model.id ?? 'qwen-2.5-coder-14b',
        quantization: top?.fit.quantization ?? 'q4_k_m',
        verdict: top?.fit.verdict ?? 'good',
        score: Math.round(top?.score ?? 50),
      };
    }),
    aggregate: { benchRuns: MACHINE_PRESETS.length, avgTps: 0, avgQuality: 0 },
    updatedAt: Date.now(),
  });
  out.push({
    slug: 'local-models/fastest-by-gpu',
    pageType: 'gpu',
    title: 'Fastest local coding models by GPU',
    subtitle: 'Pure speed ranking on NVIDIA, AMD, Apple Silicon.',
    primary: { kind: 'gpu', id: 'fastest-by-gpu' },
    topModels: GPUS.slice(0, 5).map((g) => ({
      modelId: g.id,
      quantization: 'q4_k_m' as Quantization,
      verdict: 'good' as const,
      score: Math.round(g.computeTierTps),
    })),
    aggregate: { benchRuns: GPUS.length, avgTps: 0, avgQuality: 0 },
    updatedAt: Date.now(),
  });
  out.push({
    slug: 'local-models/apple-silicon',
    pageType: 'apple-chip',
    title: 'Best local coding models for Apple Silicon',
    subtitle: 'M1 through M4, every tier.',
    primary: { kind: 'apple-chip', id: 'apple-silicon' },
    topModels: APPLE_CHIPS.slice(0, 5).map((c) => ({
      modelId: c.id,
      quantization: 'q4_k_m' as Quantization,
      verdict: 'good' as const,
      score: Math.round(c.memoryBandwidthGbps / 10),
    })),
    aggregate: { benchRuns: APPLE_CHIPS.length, avgTps: 0, avgQuality: 0 },
    updatedAt: Date.now(),
  });
  return out;
}

export function findListingBySlug(slug: string): DirectoryListing | undefined {
  return allListings().find((l) => l.slug === slug);
}

export function searchListings(query: string, limit = 12): DirectoryListing[] {
  const q = query.trim().toLowerCase();
  if (!q) return allListings().slice(0, limit);
  return allListings()
    .filter(
      (l) =>
        l.title.toLowerCase().includes(q) ||
        l.slug.toLowerCase().includes(q) ||
        l.subtitle.toLowerCase().includes(q),
    )
    .slice(0, limit);
}
